import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ExternalLink, Search } from "lucide-react";

import {
  BatchDetailLink,
  SalesOrderDetailLink,
  ShipmentDetailLink,
  WorkOrderDetailLink,
} from "@/components/hub/HubEntityDetailLink";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { CardTitleWithHelp, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { megaventoryPurchaseOrderDocumentUrl } from "@/lib/megaventoryDocumentUrls";
import { EM_DASH, cn } from "@/lib/utils";

import {
  DEVICE_LOOKUP_QUERY_KEY,
  fetchDeviceLookupReport,
  type DeviceLookupClampQc,
  type DeviceLookupEpisode,
  type DeviceLookupLaborScan,
  type DeviceLookupPotting,
  type DeviceLookupProductionTests,
  type DeviceLookupQualityIncident,
  type DeviceLookupReport,
  type DeviceLookupSerialFci,
  type DeviceLookupTouchSample,
} from "./deviceLookupApi";
import {
  productionTestScopeMessage,
  productionTestStatusTone,
  ProductionTestMatrix,
} from "./productionTestStatusUi";

function formatDate(value: string | null): string {
  if (value == null) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(value: string | null): string {
  if (value == null) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function parseEpisodeSearchParam(value: string | null): number | null {
  if (value == null || value.trim() === "") return null;
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function episodeStatusBadge(episode: DeviceLookupEpisode): BadgeTone {
  if (episode.active) return "success";
  return "neutral";
}

function episodeStatusLabel(episode: DeviceLookupEpisode): string {
  return episode.active ? "Active" : "Archived";
}

function EpisodeHistorySection({
  report,
  selectedSerialFciId,
  onSelectEpisode,
}: {
  report: DeviceLookupReport;
  selectedSerialFciId: number;
  onSelectEpisode: (serialFciId: number) => void;
}) {
  if (report.episodes.length <= 1) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitleWithHelp hoverHint="Every serial_fci row for this device identity. Select an episode to load its manufacturing detail below.">
          Configuration episodes
        </CardTitleWithHelp>
        <p className="text-sm text-slate-600">
          Device identity{" "}
          <span className="font-mono text-slate-900">{report.deviceId}</span>
          {report.activeSerialFciId != null ? (
            <>
              {" "}
              · active episode{" "}
              <span className="font-mono text-slate-900">{report.activeSerialFciId}</span>
            </>
          ) : (
            " · no active episode (showing latest archived)"
          )}
        </p>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-600">
                <th className="py-2 pr-4 font-medium">Episode</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">SKU</th>
                <th className="py-2 pr-4 font-medium">Station</th>
                <th className="py-2 pr-4 font-medium">Batch line</th>
                <th className="py-2 pr-4 font-medium">Assembled</th>
                <th className="py-2 font-medium">Archived</th>
              </tr>
            </thead>
            <tbody>
              {report.episodes.map((episode) => {
                const selected = episode.id === selectedSerialFciId;
                return (
                  <tr
                    key={episode.id}
                    className={cn(
                      "border-b border-slate-100 last:border-b-0",
                      selected ? "bg-sky-50" : "hover:bg-slate-50",
                    )}
                  >
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        className={cn(
                          "rounded px-1 py-0.5 text-left font-mono text-xs underline-offset-2 hover:underline",
                          selected ? "font-semibold text-sky-900" : "text-blue-700",
                        )}
                        onClick={() => onSelectEpisode(episode.id)}
                      >
                        {episode.id}
                      </button>
                    </td>
                    <td className="py-2 pr-4">
                      <Badge tone={episodeStatusBadge(episode)}>
                        {episodeStatusLabel(episode)}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4">{episode.finishedSku ?? EM_DASH}</td>
                    <td className="py-2 pr-4">{episode.stationName ?? EM_DASH}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{episode.batchItemId ?? EM_DASH}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDateTime(episode.dateAssembled)}</td>
                    <td className="py-2 whitespace-nowrap">{formatDateTime(episode.archivedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

function boolLabel(value: boolean | null | undefined): string {
  if (value == null) return EM_DASH;
  return value ? "Yes" : "No";
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid gap-1 border-b border-slate-100 py-2 last:border-b-0 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] sm:gap-4">
      <dt className="text-sm font-medium text-slate-600">{label}</dt>
      <dd className="text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function purchaseOrderLink(number: string | null, mvId: number | null): ReactNode {
  if (number == null && mvId == null) return EM_DASH;
  if (mvId != null) {
    const label = number?.trim() || `MV ${mvId}`;
    return (
      <a
        href={megaventoryPurchaseOrderDocumentUrl(mvId, number)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-mono text-xs text-blue-700 underline decoration-blue-700/40 underline-offset-2 hover:text-blue-900"
      >
        <span>{label}</span>
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </a>
    );
  }
  return <span className="font-mono text-xs">{number}</span>;
}

function stateBadgeTone(headline: string): BadgeTone {
  if (headline.includes("Touch sample")) return "warning";
  if (headline.includes("Scrapped")) return "warning";
  if (headline.includes("alternate") || headline.includes("Off batch") || headline.includes("Not on")) return "info";
  if (headline.includes("Packed")) return "success";
  return "neutral";
}

function IdentitySection({
  serialFci,
  touchSample,
  viewingArchivedEpisode,
}: {
  serialFci: DeviceLookupSerialFci;
  touchSample: DeviceLookupTouchSample;
  viewingArchivedEpisode: boolean;
}) {
  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitleWithHelp hoverHint="Core identifiers from serial_fci and linked build metadata.">
          Identity and build
        </CardTitleWithHelp>
        {viewingArchivedEpisode ? (
          <Badge tone="warning">Archived episode — detail below is historical</Badge>
        ) : null}
      </CardHeader>
      <CardBody>
        <dl>
          <DetailRow label="serial_fci id" value={<span className="font-mono text-xs">{serialFci.id}</span>} />
          <DetailRow label="Device label serial" value={<span className="font-mono text-xs">{serialFci.deviceLabelSerial ?? EM_DASH}</span>} />
          <DetailRow label="Finished SKU" value={serialFci.finishedSku ?? EM_DASH} />
          <DetailRow label="Firmware" value={serialFci.firmwareVersion ?? EM_DASH} />
          <DetailRow label="PCBA serial" value={<span className="font-mono text-xs">{serialFci.pcbaSerial ?? EM_DASH}</span>} />
          <DetailRow label="Clamp serial" value={<span className="font-mono text-xs">{serialFci.clampSerial ?? EM_DASH}</span>} />
          <DetailRow label="MCU UUID" value={<span className="font-mono text-xs break-all">{serialFci.mcuUuid ?? EM_DASH}</span>} />
          <DetailRow label="Date assembled" value={formatDate(serialFci.dateAssembled)} />
          <DetailRow
            label="Scrap / rebuild"
            value={`${touchSample.touchSampleAt != null && touchSample.touchSampleRecoveredAt == null ? "Touch sample" : serialFci.scrap ? "Scrap" : "Active"} / ${serialFci.rebuild ? "Rebuild" : "Normal"}`}
          />
          {touchSample.touchSampleAt != null ? (
            <DetailRow
              label="Touch sample since"
              value={formatDateTime(touchSample.touchSampleAt)}
            />
          ) : null}
          <DetailRow label="IMEI / ICCID" value={`${serialFci.imei ?? EM_DASH} / ${serialFci.iccid ?? EM_DASH}`} />
          <DetailRow label="Note" value={serialFci.note ?? EM_DASH} />
        </dl>
      </CardBody>
    </Card>
  );
}

function ManufacturingStateSection({ report }: { report: DeviceLookupReport }) {
  const { serialFci, manufacturingState } = report;
  return (
    <Card>
      <CardHeader className="space-y-3">
        <CardTitleWithHelp hoverHint="Where the device sits in batch, station, shipment, and pack-out flow, plus obvious gaps.">
          Manufacturing state
        </CardTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={stateBadgeTone(manufacturingState.headline)}>{manufacturingState.headline}</Badge>
          {serialFci.offBatchItemLabel ? <Badge tone="muted">{serialFci.offBatchItemLabel}</Badge> : null}
          {serialFci.stationName ? <Badge tone="neutral">Station: {serialFci.stationName}</Badge> : null}
        </div>
        <p className="text-sm text-slate-600">{manufacturingState.detail}</p>
      </CardHeader>
      <CardBody className="space-y-4">
        <dl>
          <DetailRow label="Batch item" value={serialFci.batchItemId != null ? `${serialFci.batchItemId}${serialFci.batchItemSku ? ` · ${serialFci.batchItemSku}` : ""}` : EM_DASH} />
          <DetailRow
            label="Batch"
            value={
              serialFci.batchId != null ? <BatchDetailLink batchId={serialFci.batchId} /> : EM_DASH
            }
          />
          <DetailRow label="Batch remark" value={serialFci.batchMfrRemark ?? EM_DASH} />
          <DetailRow
            label="Build order"
            value={
              serialFci.buildOrderId != null ? (
                <WorkOrderDetailLink
                  buildOrderId={serialFci.buildOrderId}
                  workOrderNumber={serialFci.buildOrderNumber}
                />
              ) : serialFci.buildOrderNumber != null ? (
                serialFci.buildOrderNumber
              ) : (
                EM_DASH
              )
            }
          />
          <DetailRow
            label="Shipment"
            value={
              serialFci.shipmentId != null ? (
                <ShipmentDetailLink shipmentId={serialFci.shipmentId} />
              ) : (
                EM_DASH
              )
            }
          />
          <DetailRow
            label="Sales order"
            value={
              serialFci.salesOrderId != null ? (
                <SalesOrderDetailLink
                  salesOrderId={serialFci.salesOrderId}
                  salesOrderNumber={serialFci.salesOrderNumber}
                />
              ) : serialFci.salesOrderNumber != null ? (
                serialFci.salesOrderNumber
              ) : (
                EM_DASH
              )
            }
          />
          <DetailRow label="Location" value={serialFci.locationName ?? EM_DASH} />
          <DetailRow label="Box serial" value={serialFci.boxSerial ?? EM_DASH} />
          <DetailRow label="Carton number" value={serialFci.cartonNumber ?? EM_DASH} />
        </dl>
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Checks and gaps</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            {manufacturingState.missingItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </CardBody>
    </Card>
  );
}

function PottingSection({ rows }: { rows: DeviceLookupPotting[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="quality.potting rows for this device label, including vendor purchase order linkage and grades.">
          Potting
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-600">No potting rows for this device label serial.</p>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => (
              <div key={row.id} className="rounded-lg border border-slate-200 p-4">
                <dl>
                  <DetailRow label="Potting id" value={row.id} />
                  <DetailRow label="Sent at" value={formatDateTime(row.sentAt)} />
                  <DetailRow label="Potting grade" value={row.pottingGrade ?? "Not graded"} />
                  <DetailRow
                    label="Pass"
                    value={row.pass == null ? "Not decided" : row.pass ? "Yes" : "No"}
                  />
                  <DetailRow label="Graded at" value={formatDateTime(row.gradedAt)} />
                  <DetailRow label="Graded by" value={row.gradedBy ?? EM_DASH} />
                  <DetailRow label="Purchase order" value={purchaseOrderLink(row.purchaseOrderNumber, row.purchaseOrderMvId)} />
                  <DetailRow label="PO status" value={row.purchaseOrderMvStatus ?? EM_DASH} />
                  <DetailRow label="PO line SKU" value={row.purchaseOrderItemSku ?? EM_DASH} />
                  <DetailRow label="Notes" value={row.notes ?? EM_DASH} />
                </dl>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ClampQcSection({ clampQc }: { clampQc: DeviceLookupClampQc | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Latest SGS Clamp QC inspection for the linked clamp serial.">
          Clamp QC
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        {clampQc == null ? (
          <p className="text-sm text-slate-600">No clamp QC row for this device&apos;s clamp serial.</p>
        ) : (
          <dl>
            <DetailRow label="Clamp serial" value={<span className="font-mono text-xs">{clampQc.clampSerial ?? EM_DASH}</span>} />
            <DetailRow label="SKU" value={clampQc.sku ?? EM_DASH} />
            <DetailRow label="Purchase order" value={clampQc.purchaseOrderNumber ?? EM_DASH} />
            <DetailRow label="SGS QC at" value={formatDateTime(clampQc.sgsQcAt)} />
            <DetailRow label="SGS QC by" value={clampQc.sgsQcBy ?? EM_DASH} />
            <DetailRow label="Work order" value={clampQc.sgsQcWorkOrderId ?? EM_DASH} />
            <DetailRow label="Mechanical pass" value={boolLabel(clampQc.sgsMechanicalPass)} />
            <DetailRow label="Inductance pass" value={boolLabel(clampQc.sgsInductancePass)} />
            <DetailRow label="Line harvest" value={clampQc.sgsLineHarvestPerformed ? `${boolLabel(clampQc.sgsLineHarvestPass)}${clampQc.sgsLineHarvestDrag != null ? ` · drag ${clampQc.sgsLineHarvestDrag}` : ""}` : EM_DASH} />
          </dl>
        )}
      </CardBody>
    </Card>
  );
}

function passLabel(value: boolean | null | undefined): string {
  if (value == null) return "Not recorded";
  return value ? "Pass" : "Fail";
}

function PcbaTestSection({ report }: { report: DeviceLookupReport }) {
  const { serialFci, pcbaTestRows, pcbaTestRequirementSkipped } = report;

  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitleWithHelp hoverHint="public.pcba_test rows matched by MCU UUID (mcu_id) and optionally PCBA serial (pcba_serial). Configure blocks when the MCU UUID has no rows or a null/false/ban history, or when the PCBA serial has a null/false/ban history. No PCBA serial rows does not block. Skipped for FI-5A-W* SKUs; hub backfill firmware-only is allowed on older prefixes.">
          PCBA test
        </CardTitleWithHelp>
        {pcbaTestRequirementSkipped ? (
          <p className="text-sm text-slate-600">PCBA test record is not required for this finished SKU family.</p>
        ) : null}
      </CardHeader>
      <CardBody>
        {(serialFci.mcuUuid ?? "").trim() === "" && (serialFci.pcbaSerial ?? "").trim() === "" ? (
          <p className="text-sm text-slate-600">No MCU UUID or PCBA serial on serial_fci — cannot match pcba_test rows.</p>
        ) : pcbaTestRows.length === 0 ? (
          <p className="text-sm text-slate-600">
            No pcba_test rows for MCU{" "}
            <span className="font-mono text-xs">{(serialFci.mcuUuid ?? "").trim() || "(none)"}</span>
            {" "}or PCBA serial{" "}
            <span className="font-mono text-xs">{(serialFci.pcbaSerial ?? "").trim() || "(none)"}</span>.
          </p>
        ) : (
          <div className="space-y-4">
            {pcbaTestRows.map((row, index) => (
              <div key={row.id} className="rounded-lg border border-slate-200 p-4">
                {index === 0 ? (
                  <div className="mb-3">
                    <Badge tone={row.ban === true || row.powerTestPass === false || row.firmwarePass === false ? "warning" : "success"}>
                      Latest row
                    </Badge>
                  </div>
                ) : null}
                <dl>
                  <DetailRow label="Row id" value={row.id} />
                  <DetailRow label="MCU id" value={<span className="font-mono text-xs break-all">{row.mcuId ?? EM_DASH}</span>} />
                  <DetailRow label="Power test" value={passLabel(row.powerTestPass)} />
                  <DetailRow label="Power test at" value={formatDateTime(row.powerTestTimestamp)} />
                  <DetailRow label="Firmware pass" value={passLabel(row.firmwarePass)} />
                  <DetailRow label="Firmware version" value={row.firmwareVersion ?? EM_DASH} />
                  <DetailRow label="Firmware file" value={row.firmwareFile ?? EM_DASH} />
                  <DetailRow label="Firmware at" value={formatDateTime(row.firmwareTimestamp)} />
                  <DetailRow label="Banned" value={row.ban === true ? `Yes${row.banReason ? ` — ${row.banReason}` : ""}` : boolLabel(row.ban)} />
                </dl>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function PcbaCalibrationSection({ report }: { report: DeviceLookupReport }) {
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Recent quality.pcba_calibration rows for the device PCBA serial.">
          PCBA calibration
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        {report.pcbaCalibrations.length === 0 ? (
          <p className="text-sm text-slate-600">No PCBA calibration rows for {report.serialFci.pcbaSerial ?? "this device"}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4 font-medium">Calibrated at</th>
                  <th className="py-2 pr-4 font-medium">Sequence set</th>
                  <th className="py-2 font-medium">Row id</th>
                </tr>
              </thead>
              <tbody>
                {report.pcbaCalibrations.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="py-2 pr-4">{formatDateTime(row.calibratedAt)}</td>
                    <td className="py-2 pr-4">{row.sequenceSet ?? EM_DASH}</td>
                    <td className="py-2 font-mono text-xs">{row.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ProductionTestsSection({
  productionTests,
  viewingArchivedEpisode,
}: {
  productionTests: DeviceLookupProductionTests | null;
  viewingArchivedEpisode: boolean;
}) {
  const scopeMessage = productionTestScopeMessage(productionTests);
  const history = productionTests?.history ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Effective Cal+Test production tests for this configuration episode and current valid calibration.">
          Production tests
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-4">
        {viewingArchivedEpisode ? (
          <p className="text-sm text-amber-800">
            Viewing an archived configuration episode. Effective status below is for this episode only; it does not apply to the active device configuration.
          </p>
        ) : null}
        {scopeMessage ? <p className="text-sm text-slate-600">{scopeMessage}</p> : null}
        <ProductionTestMatrix status={productionTests} />
        {history.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Result history</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-600">
                    <th className="py-2 pr-4 font-medium">Recorded at</th>
                    <th className="py-2 pr-4 font-medium">Test</th>
                    <th className="py-2 pr-4 font-medium">Outcome</th>
                    <th className="py-2 pr-4 font-medium">Run</th>
                    <th className="py-2 font-medium">Result id</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 20).map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-2 pr-4">{formatDateTime(row.recordedAt)}</td>
                      <td className="py-2 pr-4">{row.testName}</td>
                      <td className="py-2 pr-4">
                        <Badge tone={productionTestStatusTone(row.outcome === "pass" || row.outcome === "skip" ? "pass" : row.outcome === "fail" || row.outcome === "error" ? "fail" : "pending")}>
                          {row.outcome}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">
                        {row.runKind} #{row.runId}
                      </td>
                      <td className="py-2 font-mono text-xs">{row.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function LaborScansSection({ rows }: { rows: DeviceLookupLaborScan[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Resolved labor.device_scan rows for this serial_fci, newest first.">
          Labor scan history
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-600">No labor scans recorded for this device.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4 font-medium">Scanned at</th>
                  <th className="py-2 pr-4 font-medium">Station</th>
                  <th className="py-2 pr-4 font-medium">Operator</th>
                  <th className="py-2 pr-4 font-medium">Resolution</th>
                  <th className="py-2 font-medium">Allocated seconds</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDateTime(row.scannedAt)}</td>
                    <td className="py-2 pr-4">{row.stationLabel}</td>
                    <td className="py-2 pr-4">{row.userEmail ?? EM_DASH}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{row.resolutionPath}</td>
                    <td className="py-2">{row.allocatedSeconds ?? EM_DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function RelatedRecordsSection({ report }: { report: DeviceLookupReport }) {
  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Final QC quality pillars (potting grade, visual inspection, GPS check), quality incidents, and potting rework tied to this serial_fci.">
          Related quality records
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Visual inspection</h3>
          {report.finalQcVisualInspection == null ? (
            <p className="mt-2 text-sm text-slate-600">No visual inspection decision recorded.</p>
          ) : (
            <dl className="mt-2">
              <DetailRow
                label="Pass"
                value={report.finalQcVisualInspection.pass ? "Yes" : "No"}
              />
              <DetailRow label="Decided at" value={formatDateTime(report.finalQcVisualInspection.decidedAt)} />
              <DetailRow label="Decided by" value={report.finalQcVisualInspection.decidedByEmail ?? EM_DASH} />
            </dl>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">GPS check (wireless check-in)</h3>
          {report.wirelessCheckin == null ? (
            <p className="mt-2 text-sm text-slate-600">No wireless check-in row.</p>
          ) : (
            <dl className="mt-2">
              <DetailRow label="Status" value={report.wirelessCheckin.isActive ? "Active on GPS list" : "Archived"} />
              <DetailRow label="Added at" value={formatDateTime(report.wirelessCheckin.addedAt)} />
              <DetailRow label="GPS hit" value={report.wirelessCheckin.hit ?? EM_DASH} />
              <DetailRow
                label="Pass"
                value={
                  report.wirelessCheckin.pass == null
                    ? "Not decided"
                    : report.wirelessCheckin.pass
                      ? "Yes"
                      : "No"
                }
              />
              <DetailRow label="Decided at" value={formatDateTime(report.wirelessCheckin.decidedAt)} />
              <DetailRow label="Decided by" value={report.wirelessCheckin.decidedByEmail ?? EM_DASH} />
              <DetailRow label="Completed at" value={formatDateTime(report.wirelessCheckin.completedAt)} />
              <DetailRow label="Outcome" value={report.wirelessCheckin.completionOutcome ?? EM_DASH} />
            </dl>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">Quality incidents</h3>
          {report.qualityIncidents.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">No quality incidents.</p>
          ) : (
            <div className="mt-2 space-y-3">
              {report.qualityIncidents.map((incident: DeviceLookupQualityIncident) => (
                <div key={incident.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">Incident {incident.id}</span>
                    <Badge tone={incident.status === "resolved" ? "success" : "warning"}>{incident.status}</Badge>
                  </div>
                  <dl>
                    <DetailRow label="Issue" value={incident.issue ?? EM_DASH} />
                    <DetailRow label="Issue date" value={formatDate(incident.issueDate)} />
                    <DetailRow label="Issue person" value={incident.issuePerson ?? EM_DASH} />
                    <DetailRow label="Resolution" value={incident.repairScrap ?? EM_DASH} />
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-900">Potting rework</h3>
          {report.pottingReworkRows.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">No potting rework rows.</p>
          ) : (
            <div className="mt-2 space-y-3">
              {report.pottingReworkRows.map((row) => (
                <div key={row.id} className="rounded-lg border border-slate-200 p-3">
                  <dl>
                    <DetailRow label="Rework id" value={row.id} />
                    <DetailRow label="Incident id" value={row.incidentId} />
                    <DetailRow label="Routed at" value={formatDateTime(row.routedAt)} />
                    <DetailRow label="Regrade" value={row.regradePottingGrade ?? "Not graded"} />
                    <DetailRow label="Purchase order" value={purchaseOrderLink(row.purchaseOrderNumber, row.purchaseOrderMvId)} />
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function DeviceLookupReportView({
  report,
  selectedSerialFciId,
  onSelectEpisode,
}: {
  report: DeviceLookupReport;
  selectedSerialFciId: number;
  onSelectEpisode: (serialFciId: number) => void;
}) {
  const viewingArchivedEpisode = report.selectedSerialFciId !== report.activeSerialFciId;

  return (
    <div className="space-y-4">
      <EpisodeHistorySection
        report={report}
        selectedSerialFciId={selectedSerialFciId}
        onSelectEpisode={onSelectEpisode}
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <IdentitySection
          serialFci={report.serialFci}
          touchSample={report.touchSample}
          viewingArchivedEpisode={viewingArchivedEpisode}
        />
        <ManufacturingStateSection report={report} />
        <PottingSection rows={report.pottingRows} />
        <ClampQcSection clampQc={report.clampQc} />
        <PcbaTestSection report={report} />
        <PcbaCalibrationSection report={report} />
        <ProductionTestsSection
          productionTests={report.productionTests}
          viewingArchivedEpisode={viewingArchivedEpisode}
        />
        <LaborScansSection rows={report.laborScans} />
        <div className="xl:col-span-2">
          <RelatedRecordsSection report={report} />
        </div>
      </div>
    </div>
  );
}

export function DeviceLookupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSerial = searchParams.get("serial") ?? searchParams.get("label") ?? "";
  const initialEpisodeId = parseEpisodeSearchParam(searchParams.get("episode"));
  const [serialInput, setSerialInput] = useState(initialSerial);
  const [lookupSerial, setLookupSerial] = useState(initialSerial.trim());
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<number | null>(initialEpisodeId);
  const serialInputRef = useRef<HTMLInputElement>(null);

  useFloorPageScanCapture({
    inputRef: serialInputRef,
    onScan: (serial) => {
      setSerialInput(serial);
      setLookupSerial(serial);
      setSelectedEpisodeId(null);
      setSearchParams({ serial });
    },
  });

  useEffect(() => {
    const nextSerial = searchParams.get("serial") ?? searchParams.get("label") ?? "";
    setSerialInput(nextSerial);
    setLookupSerial(nextSerial.trim());
    setSelectedEpisodeId(parseEpisodeSearchParam(searchParams.get("episode")));
  }, [searchParams]);

  const lookupQuery = useQuery({
    queryKey: [DEVICE_LOOKUP_QUERY_KEY, lookupSerial, selectedEpisodeId],
    queryFn: () =>
      fetchDeviceLookupReport(lookupSerial, {
        serialFciId: selectedEpisodeId,
      }),
    enabled: lookupSerial !== "",
    retry: false,
  });

  const handleSelectEpisode = (serialFciId: number) => {
    setSelectedEpisodeId(serialFciId);
    const nextParams: Record<string, string> = { serial: lookupSerial };
    if (lookupQuery.data?.activeSerialFciId !== serialFciId) {
      nextParams.episode = String(serialFciId);
    }
    setSearchParams(nextParams);
  };

  const errorMessage = useMemo(() => {
    if (lookupSerial === "" || !lookupQuery.isError) return null;
    if (lookupQuery.error instanceof ApiError && lookupQuery.error.status === 404) {
      return `No device found for ${lookupSerial}.`;
    }
    if (lookupQuery.error instanceof Error) {
      return lookupQuery.error.message;
    }
    return "Device lookup failed.";
  }, [lookupQuery.error, lookupQuery.isError, lookupSerial]);

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp hoverHint="Search by device label serial, PCBA serial, IMEI, ICCID, MCU UUID, or box serial. Prefix with pcba:, @pcba, imei:, @imei, iccid:, @iccid, mcu:, @mcu, box:, @box, or label: to match one field only.">
          Device lookup
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              const nextSerial = serialInput.trim();
              setLookupSerial(nextSerial);
              setSelectedEpisodeId(null);
              if (nextSerial === "") {
                setSearchParams({});
              } else {
                setSearchParams({ serial: nextSerial });
              }
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="device-lookup-serial">Device identifier</Label>
              <Input
                id="device-lookup-serial"
                ref={serialInputRef}
                value={serialInput}
                onChange={(event) => setSerialInput(event.target.value)}
                placeholder="A202260020009, pcba:SD-8211-2126-00242, @imei …"
                className="font-mono"
                autoComplete="off"
                autoFocus
                {...FLOOR_PAGE_SCAN_TARGET_PROPS}
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" isLoading={lookupQuery.isFetching}>
                <Search className="h-4 w-4" aria-hidden />
                Search
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {lookupSerial === "" ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">Enter a device identifier and search to load the report.</p>
          </CardBody>
        </Card>
      ) : null}

      {errorMessage ? (
        <Card>
          <CardBody>
            <p className={cn("text-sm", lookupQuery.error instanceof ApiError && lookupQuery.error.status === 404 ? "text-slate-600" : "text-red-700")}>
              {errorMessage}
            </p>
          </CardBody>
        </Card>
      ) : null}

      {lookupQuery.data ? (
        <DeviceLookupReportView
          report={lookupQuery.data}
          selectedSerialFciId={lookupQuery.data.selectedSerialFciId}
          onSelectEpisode={handleSelectEpisode}
        />
      ) : null}
    </div>
  );
}
